import React, { useState, useEffect } from "react";
import { getEditors, createEditor, updateEditor, deleteEditor, getEditorAssignments } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Pencil, Trash, User, Briefcase, VideoCamera } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const STATUSES = ["active", "on_leave", "inactive"];

export default function Editors() {
  const { hasPermission } = useAuth();
  const [editors, setEditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingEditor, setEditingEditor] = useState(null);
  const [deletingEditor, setDeletingEditor] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    status: "active",
    specialization: "",
    max_concurrent: 3,
    color: "",
    payment_per_video: 0,
  });

  const fetchEditors = async () => {
    try {
      const response = await getEditors();
      setEditors(response.data);
    } catch (err) {
      toast.error("Failed to load editors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEditors();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleOpenCreate = () => {
    setEditingEditor(null);
    setFormData({ name: "", email: "", status: "active", specialization: "", max_concurrent: 3, color: "", payment_per_video: 0 });
    setDialogOpen(true);
  };

  const handleOpenEdit = (editor) => {
    setEditingEditor(editor);
    setFormData({
      name: editor.name,
      email: editor.email || "",
      status: editor.status,
      specialization: editor.specialization || "",
      max_concurrent: editor.max_concurrent || 3,
      color: editor.color || "",
      payment_per_video: editor.payment_per_video || 0,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEditor) {
        await updateEditor(editingEditor.id, formData);
        toast.success("Editor updated");
      } else {
        await createEditor(formData);
        toast.success("Editor created");
      }
      setDialogOpen(false);
      fetchEditors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save editor");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteEditor(deletingEditor.id);
      toast.success("Editor deleted");
      setDeleteDialogOpen(false);
      setDeletingEditor(null);
      fetchEditors();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete editor");
    }
  };

  const canCreate = hasPermission("create_editors");
  const canEdit = hasPermission("edit_editors");
  const canDelete = hasPermission("delete_editors");

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="surface-card p-6"><div className="skeleton h-32 w-full" /></div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-white">Editors</h1>
          <p className="text-gray-400 text-sm">{editors.length} editors</p>
        </div>
        {canCreate && (
          <Button onClick={handleOpenCreate} className="btn-primary" data-testid="create-editor-btn">
            <Plus size={18} className="mr-2" />
            New Editor
          </Button>
        )}
      </div>

      {/* Editor Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {editors.map((editor) => {
          const utilization = editor.max_concurrent > 0 ? (editor.current_assignments / editor.max_concurrent) * 100 : 0;
          const isOverloaded = utilization > 80;

          return (
            <div key={editor.id} className="surface-card p-6 hover:border-white/10 transition-all group" data-testid={`editor-card-${editor.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${editor.color || '#3B82F6'}30` }}>
                    <User size={24} style={{ color: editor.color || '#3B82F6' }} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">{editor.name}</h3>
                    <p className="text-sm text-gray-400">{editor.email || "No email"} {editor.payment_per_video ? `· $${editor.payment_per_video}/video` : ""}</p>
                  </div>
                </div>
                {(canEdit || canDelete) && (
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canEdit && (
                      <button onClick={() => handleOpenEdit(editor)} className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white">
                        <Pencil size={16} />
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => { setDeletingEditor(editor); setDeleteDialogOpen(true); }} className="p-2 rounded-lg hover:bg-red-500/10 text-gray-400 hover:text-red-400">
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Briefcase size={14} className="text-gray-500" />
                  <span className="text-gray-300">{editor.specialization || "No specialization"}</span>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-500">Workload</span>
                    <span className={`text-xs ${isOverloaded ? "text-amber-400" : "text-gray-400"}`}>
                      {editor.current_assignments}/{editor.max_concurrent}
                    </span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${isOverloaded ? "bg-amber-500" : "bg-blue-500"}`} style={{ width: `${Math.min(utilization, 100)}%` }} />
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-white/5">
                  <span className={`badge ${
                    editor.status === "active" ? "bg-green-500/20 text-green-400 border-green-500/30" :
                    editor.status === "on_leave" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" :
                    "bg-gray-500/20 text-gray-400 border-gray-500/30"
                  }`}>
                    {editor.status.replace("_", " ")}
                  </span>
                  <span className="text-xs text-gray-500">{editor.availability} available</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editors.length === 0 && (
        <div className="text-center py-16">
          <User size={48} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No editors yet</h3>
          <p className="text-gray-400 mb-4">Add your first editor to start assigning videos</p>
          {canCreate && (
            <Button onClick={handleOpenCreate} className="btn-primary">
              <Plus size={18} className="mr-2" />
              Add Editor
            </Button>
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1A1A2E] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">{editingEditor ? "Edit Editor" : "Add Editor"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-300">Name</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="Editor name" required data-testid="editor-name-input" />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="editor@email.com" data-testid="editor-email-input" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A2E] border-white/10">
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s} className="text-white capitalize">{s.replace("_", " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">Max Concurrent</Label>
                <Input type="number" min={1} max={10} value={formData.max_concurrent} onChange={(e) => setFormData({ ...formData, max_concurrent: parseInt(e.target.value) || 3 })} className="bg-white/5 border-white/10 text-white" data-testid="editor-max-input" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">Specialization</Label>
              <Input value={formData.specialization} onChange={(e) => setFormData({ ...formData, specialization: e.target.value })} className="bg-white/5 border-white/10 text-white" placeholder="VFX, Color Grading, etc." data-testid="editor-specialization-input" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Color</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={formData.color || "#3B82F6"} onChange={(e) => setFormData({ ...formData, color: e.target.value })} className="w-10 h-10 rounded cursor-pointer bg-transparent border-0" data-testid="editor-color-input" />
                  <span className="text-xs text-gray-400">{formData.color || "Auto"}</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Payment per Video ($)</Label>
                <Input type="number" min={0} step={0.01} value={formData.payment_per_video} onChange={(e) => setFormData({ ...formData, payment_per_video: parseFloat(e.target.value) || 0 })} className="bg-white/5 border-white/10 text-white" data-testid="editor-payment-input" />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 btn-secondary">Cancel</Button>
              <Button type="submit" className="flex-1 btn-primary" data-testid="editor-submit-btn">{editingEditor ? "Save Changes" : "Add Editor"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#1A1A2E] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Editor?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This will permanently delete "{deletingEditor?.name}". Make sure they have no assigned videos first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
